import { useCallback } from 'react';
import { type HBar, HBars } from '../components/charts.js';
import { Badge, ErrorNote, Panel, timeAgo } from '../components/ui.js';
import { type HealthRow, getHealthBoard } from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';

type Tone = 'emerald' | 'sky' | 'amber' | 'rose' | 'neutral';

const ISSUE: Record<string, { label: string; tone: Tone }> = {
  estimate_rates: { label: 'Estimate-only rates', tone: 'amber' },
  low_coverage: { label: 'Low owed coverage', tone: 'amber' },
  zero_coverage: { label: 'Zero owed coverage', tone: 'rose' },
  product_line_mismatch: { label: 'Product-line mismatch', tone: 'rose' },
  missing_rate_schedule: { label: 'No rate schedule', tone: 'rose' },
  quiet: { label: 'Went quiet', tone: 'amber' },
};

const ms = (t: number | null): number | null => t;

export function Health() {
  const { data, error, reload } = useAsyncData(useCallback(() => getHealthBoard(), []));

  return (
    <div className="space-y-6">
      <Panel title="Needs attention">
        <p className="mb-4 text-sm text-neutral-500">
          Accounts whose owed isn’t trustworthy (estimate-only rates, low coverage, mis-scored
          carriers) or that have gone quiet — surfaced so you can act before it costs a customer.
        </p>
        {error ? (
          <ErrorNote message={error} onRetry={reload} />
        ) : !data ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : (
          <>
            <div className="mb-5 flex items-baseline gap-3">
              <span className="text-3xl font-semibold tabular-nums text-neutral-900">
                {data.summary.needAttention}
              </span>
              <span className="text-sm text-neutral-500">
                of {data.summary.totalAccounts} accounts need attention
              </span>
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
                  By issue
                </div>
                <HBars
                  data={Object.entries(data.summary.byIssue).map(
                    ([k, v]): HBar => ({
                      label: ISSUE[k]?.label ?? k,
                      value: v,
                      tone: ISSUE[k]?.tone ?? 'neutral',
                    }),
                  )}
                  empty="No issues — everything looks healthy."
                />
              </div>
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Active accounts by time since last statement
                </div>
                <HBars
                  data={[
                    {
                      label: '< 2 weeks',
                      value: data.summary.quietBuckets[0] ?? 0,
                      tone: 'emerald',
                    },
                    { label: '2–6 weeks', value: data.summary.quietBuckets[1] ?? 0, tone: 'sky' },
                    {
                      label: '6–12 weeks',
                      value: data.summary.quietBuckets[2] ?? 0,
                      tone: 'amber',
                    },
                    { label: '12+ weeks', value: data.summary.quietBuckets[3] ?? 0, tone: 'rose' },
                  ]}
                  empty="No active accounts have uploaded yet."
                />
              </div>
            </div>
          </>
        )}
      </Panel>

      {data && data.accounts.length > 0 && (
        <Panel title="Accounts to review">
          <table className="w-full text-sm">
            <thead className="text-left text-neutral-500">
              <tr>
                <th className="py-1">Account</th>
                <th>Issues</th>
                <th className="text-right">Owed</th>
                <th className="text-right">Coverage</th>
                <th className="text-right">Last statement</th>
              </tr>
            </thead>
            <tbody>
              {data.accounts.map((a: HealthRow) => (
                <tr key={a.accountId} className="border-t border-neutral-100 align-top">
                  <td className="py-2 font-medium text-neutral-800">{a.name}</td>
                  <td className="py-2">
                    <div className="flex flex-wrap gap-1">
                      {a.issues.map((i) => (
                        <Badge key={i} tone={ISSUE[i]?.tone ?? 'neutral'}>
                          {ISSUE[i]?.label ?? i}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 text-right tabular-nums text-neutral-700">
                    {a.owed > 0
                      ? a.owed.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                      : '—'}
                  </td>
                  <td className="py-2 text-right tabular-nums text-neutral-700">
                    {a.coveragePct != null ? `${Math.round(a.coveragePct * 100)}%` : '—'}
                  </td>
                  <td className="py-2 text-right text-neutral-500">
                    {timeAgo(ms(a.lastJobAt))}
                    {a.weeksQuiet != null && a.weeksQuiet >= 6 && (
                      <span className="ml-1 text-rose-500">({a.weeksQuiet}w quiet)</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}
